import { fromPostgrest, guard, raise, type Result } from '@/lib/result';
import { supabase } from '@/lib/supabase';

import { mapRating, type Rating, type RatingInput } from '../model/rating';

export const ratingsRepository = {
  /** The signed-in user's rating for an application, if they've left one. */
  async myRatingForApplication(
    applicationId: string,
    raterId: string,
  ): Promise<Result<Rating | null>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('ratings')
        .select('*')
        .eq('application_id', applicationId)
        .eq('rater_id', raterId)
        .maybeSingle();
      if (error) throw fromPostgrest(error);
      return data ? mapRating(data as unknown) : null;
    });
  },

  /**
   * Phase 6 (D-014, D-012): true iff the signed-in user is the masjid's owner or
   * a masjid_members row with role='manager' — i.e. can submit the masjid-side
   * rating on this application. Server-side truth via SECURITY INVOKER RPC.
   */
  async canRateAsManager(applicationId: string): Promise<Result<boolean>> {
    return guard(async () => {
      const { data, error } = await supabase.rpc('can_rate_as_manager', {
        p_application_id: applicationId,
      });
      if (error) raise('server', error.message, error);
      return Boolean(data);
    });
  },

  async create(input: RatingInput, raterId: string): Promise<Result<Rating>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('ratings')
        .insert({
          application_id: input.applicationId,
          rater_id: raterId,
          ratee_id: input.rateeId,
          stars: input.stars,
          comment: input.comment,
        })
        .select('*')
        .single();
      if (error) throw fromPostgrest(error);
      return mapRating(data as unknown);
    });
  },
};
